import { Reveal } from "@/lib/reveal";
import { BadgeCheck, Gauge, LifeBuoy, Zap } from "lucide-react";

const ITEMS = [
  {
    icon: BadgeCheck,
    title: "Proven Strategies",
    desc: "Playbooks refined across 100+ campaigns, tuned for your vertical.",
  },
  {
    icon: Gauge,
    title: "High Conversion Focus",
    desc: "Creatives, copy, and funnels built to move the revenue needle — not vanity metrics.",
  },
  {
    icon: LifeBuoy,
    title: "24/7 Support",
    desc: "Always-on account ops — so momentum never stalls waiting on an email.",
  },
  {
    icon: Zap,
    title: "Fast Results",
    desc: "Sprints instead of endless retainers. Measurable lifts inside the first 30 days.",
  },
];

export const WhyChooseUs = () => {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 85% 20%, rgba(255,123,0,0.15), transparent 45%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF7B00]">
              — Why Mature
            </span>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              A partner, not a{" "}
              <span className="text-gradient-gold">vendor.</span>
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-md">
              We’re obsessed with outcomes. Every engagement is structured
              around four non-negotiables — the reason clients stay, scale,
              and refer us.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-10 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 pr-6">
              <div className="flex -space-x-2">
                {["#FF7B00", "#FFB347", "#FF9933"].map((c) => (
                  <span
                    key={c}
                    style={{ backgroundColor: c }}
                    className="h-9 w-9 rounded-full border-2 border-[#050505]"
                  />
                ))}
              </div>
              <div className="text-sm">
                <div className="font-bold text-white">98% retention</div>
                <div className="text-zinc-500">across active clients</div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <ul className="space-y-4">
            {ITEMS.map((it, idx) => (
              <Reveal
                key={it.title}
                as="li"
                delay={idx * 90}
                className="group relative flex gap-6 rounded-2xl border border-white/5 bg-[#0F0F0F] p-6 md:p-7 hover:border-[#FF7B00]/30 hover:-translate-y-0.5 transition-all duration-500"
              >
                <div
                  data-testid={`why-item-${it.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF7B00]/20 to-transparent border border-[#FF7B00]/20 text-[#FF7B00]"
                >
                  <it.icon className="h-7 w-7" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">
                      {it.title}
                    </h3>
                    <span className="font-display text-xs text-zinc-600">
                      0{idx + 1}
                    </span>
                  </div>
                  <p className="mt-2 text-zinc-400 leading-relaxed">
                    {it.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
